import React, { useCallback, useEffect, useState } from "react";
import { makeStyles, withStyles } from "@material-ui/core/styles";
import { clone, equals, head, pickBy, pluck } from "ramda";
import { anyTrue, isBlank, notBlank } from "../../../util/ext";
import { useProjectLink } from "../../creatorServices";
import { useIsAdmin, useIsCreatorManager } from "../../../auth/authUtils";

import Grid from "@material-ui/core/Grid";
import Container from "@material-ui/core/Container";
import { Link as RouterLink } from "react-router-dom";

import SnackAlert from "../../../components/SnackAlert";
import { useSnackAlert } from "../../../common/use-snackAlert";
import { useCollection, useCollectionUpdate } from "../collectionServices";
import {
  firstError,
  hasError,
  validateCollection,
  validateCollectionField,
} from "../collectionValidation";
import { MediaContext } from "../../common/media-context";
import Divider from "@material-ui/core/Divider";
import EditCollectionInfoColumn from "./EditCollectionInfoColumn";
import { useConfirm } from "../../../common/use-confirm";
import ConfirmDialog from "../../../components/ConfirmDialog";
import EditDescriptionColumn from "../../common/EditDescriptionColumn";
import PanelLoadingIndicator from "../../../components/PanelLoadingIndicator";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import { Dialog, DialogContent } from "@material-ui/core";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import RequestCollectionMarc from "../RequestCollectionMarc";
import EditContentGroupStatus from "../../common/EditContentGroupStatus";
import ProjectLink from "../../common/ProjectLink";
import Skeleton from "@material-ui/lab/Skeleton";
import SaveButton from "../../../components/SaveButton";
import { faKey } from "@fortawesome/pro-regular-svg-icons";
import FontAwesomeIcon from "../../../components/FontAwesomeIcon";

const styles = theme => ({
  root: {
    padding: "40px 0 0 0",
    flexGrow: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    flexWrap: "wrap",
  },

  dialogRoot: {
    padding: "0 50px",
  },

  header: {
    margin: 0,
    padding: "40px 0 20px 0",
  },

  closeButton: {
    color: theme.palette.primary.darker,
    fontWeight: "bolder",
  },

  closeButtonIcon: {
    fontSize: "3.2rem",
  },

  loadingContainer: {
    padding: theme.spacing(2, 5),
    height: "calc(100vh - 200px)",
  },

  scrollArea: {
    height: "calc(100vh - 300px)",
    overflowY: "auto",
  },

  editPanel: {
    height: "100%",
  },

  info: {
    height: "200px",
  },

  actions: {
    paddingTop: 20,
    paddingBottom: 70,
  },
});

const useStyles = makeStyles(styles);

const DialogTitle = withStyles(styles)(props => {
  const { children, collection, classes, onClose, showAlert, ...other } = props;
  const isAdmin = useIsAdmin();
  return (
    <MuiDialogTitle disableTypography className={classes.header} {...other}>
      <Grid container justify="space-between" alignItems="center">
        <Grid item container xs alignItems="center">
          <Grid item>
            <Typography variant="h6" classes={{ h6: classes.title }}>
              {children}
            </Typography>
          </Grid>

          <Grid item style={{ paddingLeft: 45 }}>
            <RequestCollectionMarc
              collectionId={collection.id}
              showAlert={showAlert}
            />
          </Grid>

          {isAdmin && notBlank(collection.id) && (
            <Grid item>
              <Button
                component={RouterLink}
                to={`/creator/manage-access/collection/${collection.id}`}
                startIcon={<FontAwesomeIcon icon={faKey} />}
              >
                Manage Access
              </Button>
            </Grid>
          )}
        </Grid>

        <Grid container item alignItems="center" justify="flex-end" xs>
          {isAdmin && (
            <Grid item xs={3}>
              <EditContentGroupStatus />
            </Grid>
          )}

          {onClose ? (
            <Grid item xs="auto">
              <IconButton
                aria-label="close"
                className={classes.closeButton}
                onClick={onClose}
              >
                <CloseIcon className={classes.closeButtonIcon} />
              </IconButton>
            </Grid>
          ) : null}
        </Grid>
      </Grid>
    </MuiDialogTitle>
  );
});

const objectDiff = media => (val, key) => !equals(media[key], val);

const CollectionEditDialog = props => {
  const { visible, onCancel, onSaveSuccess, id } = props;
  const classes = useStyles();
  const isAdmin = useIsAdmin();
  const isCreatorManager = useIsCreatorManager();

  //-- Manage the alert message
  const snackAlertHandler = useSnackAlert();
  const { showAlert } = snackAlertHandler;

  //-- Manage the confirm dialog
  const confirmHandler = useConfirm();
  const { showConfirm } = confirmHandler;

  //-- Set up overall state
  const [collection, setCollection] = useState({});
  const [initialCollection, setInitialCollection] = useState({});
  const [thumbnailUrl, setThumbnailUrl] = useState(null);
  const [errors, setErrors] = useState({});

  const result = useCollection(id);
  useEffect(() => {
    if (result.isSuccess) {
      setCollection(result.data);
      setInitialCollection(clone(result.data));
    }
  }, [result.isSuccess, result.data]);

  //-- Handle saving the content
  const collectionUpdate = useCollectionUpdate();
  const [saveDisabled, setSaveDisabled] = useState(true);
  const handleSave = useCallback(async () => {
    setSaveDisabled(true);
    const validation = validateCollection(collection);
    try {
      if (notBlank(validation)) {
        setErrors(validation);
        throw new Error("Please address missing or invalid values");
      }
      const update = pickBy(objectDiff(initialCollection), collection);

      await collectionUpdate.mutateAsync({
        resourceIds: { id: initialCollection.id },
        data: update,
      });

      setInitialCollection(collection);

      if (onSaveSuccess) {
        onSaveSuccess();
      }

      showAlert("success", ["Collection Saved"]);
    } catch (err) {
      const messages = err?.body?.errors
        ? pluck("message", err.body.errors)
        : [err.message];
      showAlert("error", messages);
    }
  }, [
    initialCollection,
    collection,
    collectionUpdate,
    showAlert,
    onSaveSuccess,
  ]);

  const handleCloseClicked = useCallback(() => {
    const validation = validateCollection(collection);
    try {
      if (notBlank(validation)) {
        setErrors(validation);
        throw new Error("Please address missing or invalid values");
      }
      const update = pickBy(objectDiff(initialCollection), collection);

      if (isBlank(update)) {
        onCancel();
      } else {
        showConfirm(
          "Discard Changes?",
          "You have made changes to this collection without saving.  Do you wish to discard these changes and go back to the collection list?",
          {
            onConfirm: () => {
              onCancel();
            },
          },
        );
      }
    } catch (err) {
      showAlert("error", [err.message]);
    }
  }, [collection, initialCollection, showAlert, showConfirm, onCancel]);

  useEffect(() => {
    const update = pickBy(objectDiff(initialCollection), collection);
    if (notBlank(update)) {
      setSaveDisabled(false);
    }
  }, [collection, initialCollection]);

  const projectLinkResults = useProjectLink(id);

  return (
    <Dialog
      fullScreen
      classes={{ paper: classes.dialogRoot }}
      open={visible}
      onClose={handleCloseClicked}
    >
      {notBlank(collection) ? (
        <MediaContext.Provider
          value={{
            initial: initialCollection,
            media: collection,
            setMedia: setCollection,
            errors: errors,
            setErrors: setErrors,
            thumbnailUrl: thumbnailUrl,
            setThumbnailUrl: setThumbnailUrl,
          }}
        >
          <DialogTitle
            id="edit-collection-dialog-title"
            onClose={handleCloseClicked}
            showAlert={showAlert}
            collection={collection}
          >
            Edit Collection
          </DialogTitle>

          <Divider />

          <DialogContent id="collection-edit" classes={{ root: classes.root }}>
            <SnackAlert handler={snackAlertHandler} />
            <ConfirmDialog handler={confirmHandler} />

            <Container
              id="collection-edit-container"
              maxWidth={false}
              disableGutters
            >
              <div className={classes.scrollArea}>
                <Grid
                  id="collection-edit-panel"
                  container
                  alignContent="flex-start"
                  className={classes.editPanel}
                >
                  <Grid item xs={12}>
                    <EditCollectionInfoColumn />
                  </Grid>

                  <Grid item xs={8} style={{ paddingTop: 25, marginTop: 25 }}>
                    <EditDescriptionColumn
                      allowHtml
                      enableLinks
                      error={hasError(errors, "description")}
                      helperText={firstError(errors, "description")}
                      onBlur={() =>
                        setErrors(
                          validateCollectionField(
                            "description",
                            collection.description,
                          ),
                        )
                      }
                    />
                  </Grid>

                  {anyTrue([isAdmin, isCreatorManager]) && (
                    <Grid
                      item
                      xs={4}
                      style={{ paddingTop: 25, paddingLeft: 15 }}
                    >
                      {projectLinkResults.isLoading ? (
                        <Skeleton />
                      ) : (
                        <ProjectLink
                          id={id}
                          showAlert={showAlert}
                          projectLink={head(projectLinkResults.data)}
                        />
                      )}
                    </Grid>
                  )}
                </Grid>
              </div>
            </Container>
          </DialogContent>
          <DialogActions classes={{ root: classes.actions }}>
            <Button onClick={handleCloseClicked}>Cancel</Button>
            <SaveButton
              loading={collectionUpdate.isLoading}
              disabled={saveDisabled}
              onClick={handleSave}
            />
          </DialogActions>
        </MediaContext.Provider>
      ) : (
        <div className={classes.loadingContainer}>
          <PanelLoadingIndicator />
        </div>
      )}
    </Dialog>
  );
};

export default CollectionEditDialog;
